import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  agentsOf,
  tierDisplayName,
  type LabeebCatalog,
  type LabeebPatchTenantResponse,
  type LabeebTenantDetail,
} from "../../api/labeeb";
import {
  metricPath,
  metricValue,
  type FleetMetricEnvelope,
} from "../../api/mcp";
import { Bars } from "../../components/charts";
import { ForbiddenNotice, Gate } from "../../components/authority/Gate";
import { ComingSoon } from "../../components/ComingSoon";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { Badge, Card, Fields, Field, Notice, Page, Panel } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../../components/write/EffectTiming";
import type { AppConfig } from "../../config";
import { Button, Checkbox, Input } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { ProductTag } from "../../products/PerProduct";

/**
 * Three surfaces the design draws for Labeeb that still have no backing
 * route, plus one — Beta grants, below — that does.
 *
 * Each of the three still here names the endpoints that must exist and what
 * the design specifies, so the gap is a work item rather than a mystery, and
 * so the intent survives the wait. When these are finally built, the
 * constraints they were designed under should not have to be rediscovered
 * from a prototype nobody can still run.
 *
 * They render the design's SHAPE, blurred. That is the whole treatment: the
 * reader sees a table or a switch bank belongs here and cannot read a value
 * out of it, so nothing on screen can be mistaken for a real merchant, tier or
 * figure. Filling them with the prototype's seeded numbers would put invented
 * merchant names and credit figures in the console that changes what
 * merchants pay.
 *
 * Why these three still have none: HITL gate state, and welcome content are
 * genuinely unbacked — `GET/PATCH /admin/hitl` and `GET/PATCH/POST
 * /admin/welcome` do not exist on Labeeb's admin API at all, in any
 * environment. That is a different absence from "not wired": Labeeb's admin
 * API IS mounted on this gateway now (see products/registry.ts), these three
 * particular routes just do not exist yet on Labeeb's side of it.
 */

/**
 * Beta grants — a per-tenant lookup, not the fleet-wide table the old blurred
 * mockup implied.
 *
 * `GET /admin/tenants` (Labeeb) lists only tenants that already have a config
 * item, and carries no more per-grant detail than the arrays below — there is
 * no route that lists grants ACROSS tenants. So the only way to read or set
 * one tenant's beta agents is to look the tenant up first, the same shape as
 * the business-reference box on `/tenants`.
 *
 * `beta_agents` is a WHOLE-LIST REPLACE on `PATCH /admin/tenant/{id}` — there
 * is no add/remove endpoint, so the control here submits the complete set
 * every time rather than pretending to toggle one agent in isolation. An
 * empty list is a legitimate submission: it revokes every beta grant for the
 * tenant, which is different from the 422 the reference doc documents for an
 * empty `stable_agents` — that is a different field on a different route
 * (a deploy-time value, not writable here at all), and this control never
 * touches it.
 *
 * NOT BUILT, deliberately: an expiry and a reason field. The old ComingSoon
 * copy described grants as "always expiring and always reasoned", but
 * `PATCH /admin/tenant/{id}` accepts neither field — it only ever applies
 * `tier_id` / `allowed_agents` / `beta_agents`. A control for a field the
 * server would silently ignore is worse than no control: it would tell an
 * operator a grant expires when nothing on this API ever un-sets it.
 */
export function BetaGrants({ config }: { config: AppConfig }) {
  const navigate = useNavigate();
  const [lookup, setLookup] = useState("");
  const [tenantId, setTenantId] = useState<string | null>(null);

  const catalog = useResource<LabeebCatalog>(config, "/labeeb/admin/catalog");
  const tenant = useResource<LabeebTenantDetail>(
    config,
    tenantId ? `/labeeb/admin/tenant/${encodeURIComponent(tenantId)}` : null,
    [tenantId],
  );

  return (
    <Page
      title="Beta grants"
      subtitle="Grant a tenant early access to agents beyond what their tier and the environment's stable set allow."
    >
      <Card
        title="Look up a tenant"
        aside={<ProductTag id="labeeb" />}
        note="Beta agents are set per tenant — there is no route that lists grants across the fleet."
      >
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const v = lookup.trim();
            if (v) setTenantId(v);
          }}
        >
          <div className="w-64">
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="Tenant id, e.g. 507077"
              aria-label="Tenant id"
            />
          </div>
          <Button type="submit" disabled={!lookup.trim()}>
            Look up
          </Button>
          {tenantId && (
            <Button
              plain
              onClick={() => navigate(`/tenants/${encodeURIComponent(tenantId)}`)}
            >
              Open in Tenants
            </Button>
          )}
        </form>
      </Card>

      {tenantId && (
        <TenantGrantPanel
          key={tenantId}
          config={config}
          tenantId={tenantId}
          catalog={catalog.state.kind === "ready" ? catalog.state.data : null}
          tenant={tenant}
        />
      )}
    </Page>
  );
}

/**
 * One tenant's grant editor, once a lookup has been submitted.
 *
 * A separate component (keyed on `tenantId` in the parent) rather than inline
 * JSX in `BetaGrants`, so every hook here resets cleanly on a new lookup
 * instead of carrying edits from the tenant looked up before it.
 */
function TenantGrantPanel({
  config,
  tenantId,
  catalog,
  tenant,
}: {
  config: AppConfig;
  tenantId: string;
  catalog: LabeebCatalog | null;
  tenant: ReturnType<typeof useResource<LabeebTenantDetail>>;
}) {
  const s = tenant.state;
  const write = useWrite<LabeebPatchTenantResponse>(config);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<LabeebPatchTenantResponse | null>(null);

  const agents = agentsOf(catalog);
  // `selected` is null until the operator has touched a checkbox — that is
  // what lets the panel show the STORED list (which may be `null`, meaning
  // no grants) while still being able to submit an edited one, including an
  // edited list of zero agents, without confusing "nothing touched yet" with
  // "touched down to nothing".
  const stored = s.kind === "ready" ? (s.data.beta_agents ?? []) : [];
  const current = selected ?? stored;
  const dirty =
    selected !== null &&
    (selected.length !== stored.length || selected.some((a) => !stored.includes(a)));

  function toggle(agent: string, checked: boolean) {
    const base = selected ?? stored;
    setSelected(checked ? [...base, agent] : base.filter((a) => a !== agent));
  }

  function submit() {
    void write
      .submit(`/labeeb/admin/tenant/${encodeURIComponent(tenantId)}`, {
        method: "PATCH",
        body: JSON.stringify({ beta_agents: current }),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setOpen(false);
        setSelected(null);
        tenant.reload();
      });
  }

  return (
    <div className="mt-6 flex flex-col gap-4">
      {s.kind === "loading" && <Loading what={`tenant ${tenantId}`} />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what={`tenant ${tenantId}`} message={s.error.message} onRetry={tenant.reload} />
      )}
      {s.kind === "notfound" && (
        <LoadFailed what={`tenant ${tenantId}`} message="No such tenant." />
      )}
      {(s.kind === "invalid" || s.kind === "error") && (
        <LoadFailed what={`tenant ${tenantId}`} message={s.error.message} />
      )}

      {s.kind === "ready" && (
        <Card
          title={<span className="ref">{tenantId}</span>}
          aside={<ProductTag id="labeeb" />}
          note={
            s.data.has_config_item
              ? "Has a TenantConfigs item."
              : "No TenantConfigs item — on defaults. Setting beta agents creates one."
          }
        >
          <Fields>
            <Field label="Stored tier">
              {s.data.tier_id ? (
                <span className="ref">{tierDisplayName(catalog, s.data.tier_id)}</span>
              ) : (
                <span className="text-slate-400 dark:text-slate-500">not set — resolves to free</span>
              )}
            </Field>
            <Field label="Allowed agents (app ceiling)">
              {s.data.allowed_agents === null ? (
                <span className="text-slate-400 dark:text-slate-500">
                  not narrowed — every routable agent the tier allows
                </span>
              ) : s.data.allowed_agents.length === 0 ? (
                <span className="text-slate-400 dark:text-slate-500">narrowed to none</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {s.data.allowed_agents.map((a) => (
                    <Badge key={a}>{a}</Badge>
                  ))}
                </div>
              )}
            </Field>
          </Fields>

          {s.data.note && (
            <div className="mt-3">
              <Notice>{s.data.note}</Notice>
            </div>
          )}

          <div className="mt-5">
            <p className="mb-2 text-xs font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
              Beta agents — unioned in after tier and allowed-agents, bypassing both
            </p>

            {agents.length === 0 ? (
              <Notice tone="amber" title="Catalog has no routable agents">
                Nothing to grant — the catalog response carried an empty or missing agent list.
              </Notice>
            ) : (
              <Panel className="p-4">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {agents.map((a) => (
                    <label key={a} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={current.includes(a)}
                        onChange={(checked) => toggle(a, checked)}
                        aria-label={`Grant ${a} as a beta agent`}
                      />
                      <span className="ref">{a}</span>
                    </label>
                  ))}
                </div>
              </Panel>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {dirty
                  ? `${current.length} agent${current.length === 1 ? "" : "s"} selected, not saved.`
                  : "This is the complete list — saving replaces it entirely, it does not add to it."}
              </p>
              <div className="flex gap-2">
                {dirty && (
                  <Button outline onClick={() => setSelected(null)}>
                    Discard
                  </Button>
                )}
                <Gate need="tenant:write">
                  <Button onClick={() => setOpen(true)} disabled={!dirty}>
                    Review grant
                  </Button>
                </Gate>
              </div>
            </div>
          </div>

          {write.state.kind === "denied" && (
            <div className="mt-3">
              <ForbiddenNotice message={write.state.error.message} />
            </div>
          )}
          {/* THE 422 case Task 2 makes possible: Labeeb's nested
              {detail: {code, message}} error is now unwrapped by client.ts
              before Unprocessable is constructed, so `message` here is the
              server's own sentence — e.g. naming an agent the catalog does
              not list as routable — rather than a generic failure. */}
          {write.state.kind === "invalid" && (
            <div className="mt-3">
              <Notice tone="red" title="Refused — nothing was written">
                {write.state.error.message}
              </Notice>
            </div>
          )}
          {(write.state.kind === "unavailable" || write.state.kind === "error") && (
            <div className="mt-3">
              <Notice tone="red" title="The write did not go through">
                {write.state.error.message}
              </Notice>
            </div>
          )}

          {result && (
            <div className="mt-3">
              <WriteResult title="Beta agents updated" onDismiss={() => setResult(null)}>
                <p>
                  {(result.changed ?? []).includes("beta_agents")
                    ? "beta_agents written. "
                    : "Applied — the response named no changed keys. "}
                  {/* Always the API's OWN number when it sends one — a
                      hardcoded "~60s" is how a console lies about a value the
                      response already carries. `takes_effect` is per-key
                      (`{"AllowedAgents": "≤60s …"}` in the reference doc), so
                      render the row for the field this control actually
                      writes; fall back to the doc's own documented figure
                      only when the response omits the map entirely. */}
                  {result.takes_effect?.BetaAgents ??
                    result.takes_effect?.AllowedAgents ??
                    effectFromSeconds(60, "The change")}
                </p>
                {result.beta_note && (
                  <p className="mt-2">
                    <Badge tone="amber">bypasses the stability ceiling</Badge>{" "}
                    <span className="ml-1">{result.beta_note}</span>
                  </p>
                )}
              </WriteResult>
            </div>
          )}

          <ConfirmDialog
            open={open}
            title={`Set beta agents for ${tenantId}`}
            confirmLabel="Apply"
            busy={write.state.kind === "submitting"}
            onCancel={() => setOpen(false)}
            onConfirm={submit}
            effect="Agent changes reach the runtime within ~60s. This page shows the new list immediately; a merchant mid-conversation may not see it until their next turn."
          >
            <p>
              This <strong>replaces</strong> the tenant&rsquo;s complete beta-agent list —
              it is not additive.
            </p>
            <p className="mt-2 text-xs">
              <span className="ref">{stored.length === 0 ? "(none)" : stored.join(", ")}</span>
              {" → "}
              <span className="ref">{current.length === 0 ? "(none)" : current.join(", ")}</span>
            </p>
            <p className="mt-2 text-xs">
              Every agent granted here is unioned in AFTER the tier and allowed-agents
              ceiling — it can make routable an agent this environment has not made
              stable.
            </p>
          </ConfirmDialog>
        </Card>
      )}
    </div>
  );
}

export function HitlGates() {
  return (
    <ComingSoon
      what="HITL gates"
      product="labeeb"
      shape="switches"
      blockers={[
        {
          label: "GET/PATCH /labeeb/admin/hitl",
          detail:
            "Gate state and the approval and rejection counts are Labeeb's. Nothing on MCP's admin API exposes them, and a gate is not a tool cap — capping a tool to 0 refuses it outright rather than routing it to a human.",
        },
      ]}
      designNote="Per-tenant gating is an Operator action; removing a gate fleet-wide is Admin and dual-controlled — turning a gate off is the direction that can cause harm."
    />
  );
}

export function WelcomeContent() {
  return (
    <ComingSoon
      what="Greeting & chips"
      product="labeeb"
      shape="editor"
      blockers={[
        {
          label: "GET/PATCH/POST /labeeb/admin/welcome",
          detail:
            "Draft content, a publish step and version history, in English and Arabic. All Labeeb-side; no endpoint exists on this gateway.",
        },
      ]}
      designNote="Display copy and the dispatched utterance are separate fields — what the chip says and what it sends are not the same string. Publishing to a test tenant is Operator; publishing to the fleet is Admin and dual-controlled."
    />
  );
}

/**
 * The two MCP metrics this page can actually show, live — the ones the
 * design's blurred evidence panel below asks for that happen to already
 * exist: which tools the fleet is spending credits on, and how many tools
 * on each tier have their own cap row rather than inheriting `*`. Neither is
 * Snowflake-shaped (no histograms, no cost months, no cohorts), but both are
 * real, unread anywhere else, and belong on a page whose whole subject is
 * fleet-wide evidence.
 *
 * A SEPARATE component from `ComingSoon`, deliberately: `ComingSoon` is
 * asserted (ComingSoon.test.ts) to never fetch, so nothing invented can ever
 * reach it. This panel fetches on purpose, and sits below the blurred
 * Labeeb/Snowflake shape rather than inside it — real evidence, next to an
 * honest gap, never blended into one.
 */
function McpEvidence({ config }: { config: AppConfig }) {
  const topTools = useResource<FleetMetricEnvelope<"top_tools_today">>(
    config,
    metricPath("top_tools_today"),
  );
  const overrides = useResource<FleetMetricEnvelope<"quota_overrides_count">>(
    config,
    metricPath("quota_overrides_count"),
  );

  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      <Card
        title="Top tools today"
        note="By credits spent · UTC day · MCP, not Snowflake"
        aside={<ProductTag id="mcp" />}
      >
        {topTools.state.kind === "loading" && <Loading what="top tools" />}
        {topTools.state.kind === "signedout" && <SignedOut />}
        {topTools.state.kind === "denied" && <Denied message={topTools.state.error.message} />}
        {topTools.state.kind === "unavailable" && (
          <CouldNotLook
            what="top tools"
            message={topTools.state.error.message}
            onRetry={topTools.reload}
          />
        )}
        {topTools.state.kind === "notfound" && (
          <LoadFailed what="top tools" message="Route not found." />
        )}
        {(topTools.state.kind === "invalid" || topTools.state.kind === "error") && (
          <LoadFailed what="top tools" message={topTools.state.error.message} />
        )}
        {topTools.state.kind === "ready" &&
          (() => {
            const rows = metricValue(topTools.state) ?? [];
            return rows.length > 0 ? (
              <Bars
                rows={rows.map((r) => ({
                  label: r.tool,
                  value: r.credits,
                  note: `${r.credits} credits`,
                  tone: "brand" as const,
                }))}
                max={Math.max(...rows.map((r) => r.credits))}
              />
            ) : (
              <Notice title="No spend yet today">Nothing has been billed since UTC midnight.</Notice>
            );
          })()}
      </Card>

      <Card
        title="Cap overrides by tier"
        note="Tools with their own row, not inheriting the tier's * default"
        aside={<ProductTag id="mcp" />}
      >
        {overrides.state.kind === "loading" && <Loading what="cap overrides" />}
        {overrides.state.kind === "signedout" && <SignedOut />}
        {overrides.state.kind === "denied" && <Denied message={overrides.state.error.message} />}
        {overrides.state.kind === "unavailable" && (
          <CouldNotLook
            what="cap overrides"
            message={overrides.state.error.message}
            onRetry={overrides.reload}
          />
        )}
        {overrides.state.kind === "notfound" && (
          <LoadFailed what="cap overrides" message="Route not found." />
        )}
        {(overrides.state.kind === "invalid" || overrides.state.kind === "error") && (
          <LoadFailed what="cap overrides" message={overrides.state.error.message} />
        )}
        {overrides.state.kind === "ready" &&
          (() => {
            const byTier = metricValue(overrides.state) ?? {};
            const tiers = Object.keys(byTier).sort();
            return tiers.length > 0 ? (
              <Bars
                rows={tiers.map((t) => ({
                  label: t,
                  value: byTier[t] ?? 0,
                  note: "own rows",
                  tone: "brand" as const,
                }))}
                max={Math.max(...tiers.map((t) => byTier[t] ?? 0), 1)}
              />
            ) : (
              <Notice title="No overrides">Every tier resolves entirely through its * default.</Notice>
            );
          })()}
      </Card>
    </div>
  );
}

export function FleetEvidence({ config }: { config: AppConfig }) {
  return (
    <>
      <ComingSoon
        what="Fleet evidence"
        product="labeeb"
        shape="chart"
        blockers={[
          {
            label: "A warehouse query surface",
            detail:
              "The design's histograms, cost months and cohort figures come from Snowflake, not from a product API. Nothing on this gateway reaches it, and the console talks to product APIs only — never their tables.",
          },
          {
            label: "A fleet route on Labeeb's admin API",
            detail:
              "Messages, profiled users and consumption against the envelope are Labeeb's. MCP's per-metric route (GET /admin/metrics/{metric}) serves credits and admin actions for the current UTC day, which is a different question.",
          },
        ]}
        designNote="The design is explicit that when the warehouse is unavailable the cap preview cannot render and the write is blocked rather than bypassed — the evidence is a precondition for the write, not a decoration on it."
      />

      {/* Real, not blurred: MCP's half of "fleet evidence" is live, and having
          fetched it, hiding it behind the same blur as Labeeb's missing half
          would understate what this console can already show. */}
      <McpEvidence config={config} />
    </>
  );
}
